// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0
// DGC: search accepts D… / legacy C… display addresses (normalize to 0x hex for RPC).

import { isSuiNSName, useSuiNSEnabled } from '@mysten/core';
import { useSuiClientQuery, useSuiClient } from '@mysten/dapp-kit';
import { type SuiClient, type SuiSystemStateSummary } from '@mysten/sui.js/client';
import {
	isValidTransactionDigest,
	isValidSuiAddress,
	isValidSuiObjectId,
	normalizeSuiObjectId,
} from '@mysten/sui.js/utils';
import { useQuery } from '@tanstack/react-query';

import {
	looksLikeCpcDisplayAddress,
	tryFormatCpcAddress,
	tryNormalizeToHexAddress,
} from '~/utils/cpcAddress';

const isGenesisLibAddress = (value: string): boolean => /^(0x|0X)0{0,39}[12]$/.test(value);

type Results = { id: string; label: string; type: string }[];

/** Resolve user query to RPC hex address when possible (D… / C… / 0x hex). */
function resolveAccountHex(query: string): string | null {
	const fromDisplay = tryNormalizeToHexAddress(query);
	if (fromDisplay && isValidSuiAddress(fromDisplay) && !isGenesisLibAddress(fromDisplay)) {
		return fromDisplay;
	}
	if (looksLikeCpcDisplayAddress(query)) {
		return null;
	}
	try {
		const normalized = normalizeSuiObjectId(query);
		if (isValidSuiAddress(normalized) && !isGenesisLibAddress(normalized)) {
			return normalized;
		}
	} catch {
		/* ignore */
	}
	return null;
}

const getResultsForTransaction = async (client: SuiClient, query: string) => {
	if (!isValidTransactionDigest(query)) return null;
	const txdata = await client.getTransactionBlock({ digest: query });
	return [
		{
			id: txdata.digest,
			label: txdata.digest,
			type: 'transaction',
		},
	];
};

const getResultsForObject = async (client: SuiClient, query: string) => {
	if (looksLikeCpcDisplayAddress(query)) return null;
	const normalized = normalizeSuiObjectId(query);
	if (!isValidSuiObjectId(normalized)) return null;

	const { data, error } = await client.getObject({ id: normalized });
	if (!data || error) return null;

	return [
		{
			id: data.objectId,
			label: data.objectId,
			type: 'object',
		},
	];
};

const getResultsForCheckpoint = async (client: SuiClient, query: string) => {
	// Checkpoint digests have the same format as transaction digests:
	if (!isValidTransactionDigest(query)) return null;

	const { digest } = await client.getCheckpoint({ id: query });
	if (!digest) return null;

	return [
		{
			id: digest,
			label: digest,
			type: 'checkpoint',
		},
	];
};

const getResultsForAddress = async (_client: SuiClient, query: string, suiNSEnabled: boolean) => {
	if (suiNSEnabled && isSuiNSName(query)) {
		const resolved = await _client.resolveNameServiceAddress({ name: query.toLowerCase() });
		if (!resolved) return null;
		return [
			{
				id: resolved,
				label: resolved,
				type: 'address',
			},
		];
	}

	const normalized = resolveAccountHex(query);
	if (!normalized) return null;

	const displayLabel = tryFormatCpcAddress(normalized);
	return [
		{
			id: normalized,
			label: displayLabel,
			type: 'address',
		},
	];
};

// Query for validator by pool id or sui address.
const getResultsForValidatorByPoolIdOrSuiAddress = async (
	systemStateSummery: SuiSystemStateSummary | null,
	query: string,
) => {
	const accountHex = resolveAccountHex(query);
	let objectId: string | null = null;
	if (!looksLikeCpcDisplayAddress(query)) {
		try {
			const normalized = normalizeSuiObjectId(query);
			if (isValidSuiObjectId(normalized)) objectId = normalized;
		} catch {
			/* ignore */
		}
	}

	if ((!accountHex && !objectId) || !systemStateSummery) return null;

	const validator = systemStateSummery.activeValidators?.find(({ stakingPoolId, suiAddress }) => {
		if (objectId && stakingPoolId === objectId) return true;
		if (accountHex && (suiAddress === accountHex || suiAddress === query)) return true;
		return false;
	});

	if (!validator) return null;

	const label = accountHex ? tryFormatCpcAddress(accountHex) : objectId!;
	return [
		{
			id: validator.suiAddress || validator.stakingPoolId,
			label,
			type: 'validator',
		},
	];
};

export function useSearch(query: string) {
	const client = useSuiClient();
	const { data: systemStateSummery } = useSuiClientQuery('getLatestSuiSystemState');
	const suiNSEnabled = useSuiNSEnabled();

	return useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: ['search', query],
		queryFn: async () => {
			const results = (
				await Promise.allSettled([
					getResultsForTransaction(client, query),
					getResultsForCheckpoint(client, query),
					getResultsForAddress(client, query, suiNSEnabled),
					getResultsForObject(client, query),
					getResultsForValidatorByPoolIdOrSuiAddress(systemStateSummery || null, query),
				])
			).filter((r) => r.status === 'fulfilled' && r.value) as PromiseFulfilledResult<Results>[];

			return results.map(({ value }) => value).flat();
		},
		enabled: !!query,
		gcTime: 10000,
	});
}
